import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Form,
  notification,
  Radio,
  RadioChangeEvent,
  Space,
  Typography,
  Upload,
  UploadFile,
  UploadProps
} from 'antd'
import type { RcFile } from 'antd/es/upload/interface'
import clsx from 'clsx'
import useUpload from 'hooks/useUpload'
import { useRecoilState } from 'recoil'
import { uploadingFiles } from 'store/buildingStore'
import {
  BuildingFile,
  CustomUploadFile,
  FileMagicNumber,
  Folders,
  SelectedFiles,
  UploadingFiles,
  UploadStatus
} from 'types/building'
import Utils from 'utils'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'
import UploadStatusIcon from 'components/atoms/UploadStatusIcon'
import DeleteIcon from 'components/icons/DeleteIcon'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'

const { Dragger } = Upload

type Props = {
  documentBreadcrumb: { doc: Folders; subDoc?: Folders } | undefined
  handleSwitchMenu: () => void
  selectedFolder: { doc: Folders; subDoc?: Folders } | undefined
  folderId: number | undefined
  existingFiles: BuildingFile[]
}

export default function UploadFileMenu({
  documentBreadcrumb,
  handleSwitchMenu,
  selectedFolder,
  folderId,
  existingFiles
}: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const typeErrorRef = useRef<boolean>(false)
  const sizeErrorRef = useRef<boolean>(false)
  const pdfSizeErrorRef = useRef<boolean>(false)
  // Use a counter ref to guarantee unique batch IDs regardless of deletions
  const batchIdCounterRef = useRef<number>(0)
  const [selectedFiles, setSelectedFiles] =
    useRecoilState<SelectedFiles[]>(uploadingFiles)
  const [duplicateFiles, setDuplicateFiles] = useState<
    { newFile: CustomUploadFile; oldFile: BuildingFile }[]
  >([])
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [selectedDuplicateOption, setSelectedDuplicateOption] =
    useState<number>(1)
  const currentBatchRef = useRef<SelectedFiles | null>(null)

  const { id } = useParams()

  const category = useMemo(() => {
    if (documentBreadcrumb && documentBreadcrumb.doc.isFileHolder) {
      return documentBreadcrumb.doc.id
    }
    return documentBreadcrumb?.subDoc?.categoryId || 0
  }, [documentBreadcrumb])

  const { uploadFile, reUpload, cancelUpload, removeSuccessFile } = useUpload({
    buildingId: id || '',
    category,
    folderId,
    existingFiles
  })

  const dataSource: SelectedFiles[] = useMemo(
    () =>
      selectedFiles.filter(
        (item) => item.buildingId === id && item.category === category
      ),
    [selectedFiles, category, id]
  )

  const handleOpenDialog = () => {
    const fileUpload = document.getElementById('building-file-upload')
    fileUpload && fileUpload.click()
  }

  const typesImage = ['jpg', 'jpeg', 'png']
  const typesVideo = ['mov', 'mp4']
  const fullTypes = ['jpg', 'jpeg', 'png', 'pdf', 'mp4', 'mov']

  const isTypeInclude = (name: string, types: string[]) => {
    return Boolean(
      types.find((type) => name.split('.').pop()?.toLowerCase().includes(type))
    )
  }

  const validateArray = (filename: string) => {
    if (isTypeInclude(filename, typesImage)) return FileMagicNumber.IMAGE
    if (isTypeInclude(filename, typesVideo)) return FileMagicNumber.VIDEO
    return FileMagicNumber.PDF
  }

  const isTypeError = (filename: string) => {
    return (
      (selectedFolder?.doc?.id === 1 && !isTypeInclude(filename, typesImage)) ||
      (selectedFolder?.doc?.id !== 1 && !isTypeInclude(filename, fullTypes))
    )
  }

  const props: UploadProps = {
    name: 'building-file-upload',
    multiple: true,
    showUploadList: false,
    beforeUpload: async (
      file: File & { uid: string },
      fileList: (File & { uid: string })[]
    ) => {
      if (fileList[fileList.length - 1].uid === file.uid) {
        const files: UploadingFiles[] = []
        const batchNames: string[] = []

        try {
          for (let i = 0; i < fileList.length; i += 1) {
            const currentFile = fileList[i]
            let skip = false
            if (isTypeError(currentFile.name) && !skip) {
              typeErrorRef.current = true
              skip = true
            }
            if (
              isTypeInclude(currentFile.name, typesImage) &&
              currentFile.size / (1024 * 1024) > 50 &&
              !skip
            ) {
              sizeErrorRef.current = true
              skip = true
            }
            // Fix 5: Giới hạn PDF ≤ 100MB riêng biệt (thay vì chặn ở 60MB chung)
            if (
              isTypeInclude(currentFile.name, ['pdf']) &&
              currentFile.size / (1024 * 1024) > 100 &&
              !skip
            ) {
              pdfSizeErrorRef.current = true
              skip = true
            }
            if (
              !isTypeInclude(currentFile.name, typesImage) &&
              !isTypeInclude(currentFile.name, ['pdf']) &&
              currentFile.size / (1024 * 1024) > 60 &&
              !skip
            ) {
              sizeErrorRef.current = true
              skip = true
            }
            if (currentFile.size / (1024 * 1024) > 1000 && !skip) {
              sizeErrorRef.current = true
              skip = true
            }
            if (!skip) {
              const lastDotIndex = currentFile.name.lastIndexOf('.')
              const extension =
                lastDotIndex !== -1
                  ? currentFile.name.substring(lastDotIndex)
                  : ''
              const baseName =
                lastDotIndex !== -1
                  ? currentFile.name.substring(0, lastDotIndex)
                  : currentFile.name
              let finalName = currentFile.name
              let index = 1

              while (batchNames.includes(finalName)) {
                finalName = `${baseName}(${index})${extension}`
                index += 1
              }
              batchNames.push(finalName)

              // Fix: Create a new File object because File.name is read-only
              const renamedFile = new File([currentFile], finalName, {
                type: currentFile.type,
                lastModified: currentFile.lastModified
              }) as unknown as CustomUploadFile

              renamedFile.uid = currentFile.uid
              renamedFile.originalName = currentFile.name
              // Set originFileObj so that useUpload can create a blob URL
              // for optimistic thumbnail display before server generates the real one
              renamedFile.originFileObj = currentFile as unknown as RcFile

              // eslint-disable-next-line no-await-in-loop
              await Utils.getFileMagicNumber(
                currentFile,
                validateArray(currentFile.name)
              )
                .then(() => {
                  files.push({
                    file: renamedFile,
                    uploadStatus: UploadStatus.LOADING,
                    abortSignal: new AbortController()
                  })
                })
                .catch(() => {
                  notification.error({
                    key: 'brokenFileMessage',
                    message: t('translation.brokenFileMessage')
                  })
                })
            }
          }
        } catch (loopError) {
          // eslint-disable-next-line no-console
          console.error(
            '[MenuUploadFile] CRASH inside file processing loop',
            loopError
          )
        }

        if (typeErrorRef.current) {
          notification.error({
            message:
              selectedFolder?.doc?.id === 1
                ? t('common.validationError.building360ImageValidate')
                : t('common.validationError.buildingFileNotAllowed')
          })
          typeErrorRef.current = false
        }
        if (pdfSizeErrorRef.current) {
          notification.error({
            message: t('common.validationError.pdfTooLarge')
          })
          pdfSizeErrorRef.current = false
        }
        if (sizeErrorRef.current) {
          const error = isTypeInclude(file.name, typesImage)
            ? t('common.validationError.fileTooLarge')
            : t('common.validationError.buildingFileTooLarge')
          notification.error({
            message: error
          })
          sizeErrorRef.current = false
        }

        // Generate a unique batch ID using an incrementing counter ref
        batchIdCounterRef.current += 1
        const newBatchId = batchIdCounterRef.current

        // Collect names of files currently in-flight (actively uploading, not yet confirmed by server)
        const inFlightFileNames = new Set(
          selectedFiles
            .filter(
              (batch) =>
                batch.buildingId === `${id}` && batch.category === category
            )
            .flatMap((batch) => batch.files)
            .filter((item) => item.uploadStatus === UploadStatus.LOADING)
            .map((item) => item.file.name)
        )

        // Separate in-flight duplicates (cannot overwrite, just skip them silently with info toast)
        const inFlightDuplicateNames: string[] = []
        const nonInFlightFiles = files.filter((item) => {
          if (inFlightFileNames.has(item.file.name)) {
            inFlightDuplicateNames.push(item.file.name)
            return false
          }
          return true
        })

        if (inFlightDuplicateNames.length > 0) {
          notification.info({
            key: 'inFlightDuplicateMessage',
            message: t('building.duplicateFileModal.inFlightDuplicate', {
              fileName: inFlightDuplicateNames.join(', ')
            })
          })
        }

        // If all files were skipped as in-flight duplicates, do nothing
        if (nonInFlightFiles.length === 0) {
          form.resetFields()
          return false
        }

        const newBatch: SelectedFiles = {
          id: newBatchId,
          buildingId: `${id}`,
          category,
          files: nonInFlightFiles
        }
        currentBatchRef.current = newBatch
        setSelectedFiles((prev) => [...prev, newBatch])

        // Duplicate check logic at FE to prevent files already on server (Spec 2)
        const duplicateMatches: {
          newFile: CustomUploadFile
          oldFile: BuildingFile
        }[] = []

        // Combine files already on server with files successfully uploaded in current session
        // to bridge the gap while server list is revalidating.
        const sessionSuccessFiles: BuildingFile[] = selectedFiles
          .filter(
            (batch) =>
              batch.buildingId === `${id}` && batch.category === category
          )
          .flatMap((batch) => batch.files)
          .filter((item) => item.uploadStatus === UploadStatus.SUCCESS)
          .map((item) => ({
            id: item.id || 0,
            name: item.file.name,
            s3Path: '', // Minimal fields needed for duplicate check
            mimeType: item.file.type || '',
            extension: item.file.name.split('.').pop() || '',
            createdAt: '',
            thumbnail: '',
            pointId: null,
            takenAt: null
          }))

        const allExistingFiles = [...existingFiles, ...sessionSuccessFiles]

        nonInFlightFiles.forEach((item) => {
          const f = item.file
          const match = allExistingFiles.find((ef) => ef.name === f.name)
          if (match) {
            duplicateMatches.push({ newFile: f, oldFile: match })
          }
        })

        if (duplicateMatches.length > 0) {
          setDuplicateFiles(duplicateMatches)
          setShowDuplicateModal(true)
          setSelectedDuplicateOption(1)
        } else {
          uploadFile(undefined, undefined, newBatch)
        }
        form.resetFields()
      }
      return false
    },
    fileList: []
  }

  const normFile = ({ fileList }: { fileList: UploadFile[] }) => {
    return fileList
  }
  const handleConfirmDuplicate = () => {
    // Pass all duplicate lists to the hook for processing
    uploadFile(
      selectedDuplicateOption,
      duplicateFiles,
      currentBatchRef.current || undefined
    )
    setShowDuplicateModal(false)
  }

  const handleCancelDuplicate = () => {
    if (currentBatchRef.current) {
      setSelectedFiles((prev) =>
        prev.filter((item) => item.id !== currentBatchRef.current?.id)
      )
    }
    setShowDuplicateModal(false)
    currentBatchRef.current = null
  }

  useEffect(() => {
    removeSuccessFile()
    return () => {
      removeSuccessFile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div
        className={clsx(
          'grid  p-[10px] overflow-auto',
          {
            'grid-rows-[auto_1fr]': !dataSource.length
          },
          {
            'grid-rows-[auto_auto_1fr]': dataSource.length
          }
        )}
      >
        <div
          className="flex gap-3 pb-[10px] cursor-pointer"
          onClick={handleSwitchMenu}
        >
          <LeftArrowIcon />
          <Typography className="text-white font-[700] text-[14px] my-auto overflow-hidden">
            {documentBreadcrumb?.doc?.label}
            {documentBreadcrumb?.subDoc ? (
              <>&nbsp;&gt;&nbsp;{documentBreadcrumb.subDoc.label}</>
            ) : (
              ''
            )}
          </Typography>
        </div>

        <Form form={form}>
          <Form.Item
            name="dragger"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            noStyle
          >
            <Dragger
              id="building-file-upload"
              {...props}
              openFileDialogOnClick={false}
            >
              <div
                className={clsx(
                  'flex flex-col gap-3 items-center justify-center',
                  { 'h-[118px]': dataSource.length }
                )}
              >
                <Typography className="w-full text-left text-white font-medium text-[10px] px-[10px] leading-[16px]">
                  {t('building.imageUploadPlaceholder')}
                </Typography>
                <Button
                  onClick={handleOpenDialog}
                  type="primary"
                  className="cursor-pointer"
                  sizing="h-[40px] w-[160px]"
                >
                  {t('building.selectFile')}
                </Button>
              </div>
            </Dragger>
          </Form.Item>
        </Form>

        {dataSource.length ? (
          <div className="overflow-y-auto">
            {dataSource.map((file) =>
              file.files.map((item) => (
                <div
                  key={item?.file.uid}
                  className="grid grid-cols-[1fr_auto] gap-1 border-0 border-b border-solid theme-border-secondary py-2 px-3"
                >
                  <div className="grid">
                    <Typography className="text-[13px] leading-[19.5px] text-white">
                      {item?.file.name}
                    </Typography>
                    {item.uploadStatus === UploadStatus.FAIL && (
                      <Typography className="text-[13px] leading-[19.5px] text-[#EC3C3C]">
                        {t('building.uploadFail')}
                      </Typography>
                    )}
                  </div>

                  <div className="flex gap-1 items-center">
                    <div
                      className={clsx('flex items-center', {
                        'cursor-pointer':
                          item.uploadStatus === UploadStatus.FAIL
                      })}
                      onClick={() =>
                        item.uploadStatus === UploadStatus.FAIL &&
                        reUpload(
                          file.id,
                          item.file,
                          item.overwriteID,
                          item.mode
                        )
                      }
                    >
                      <UploadStatusIcon status={item.uploadStatus} />
                    </div>

                    <div
                      className="cursor-pointer"
                      onClick={cancelUpload(file.id, item)}
                    >
                      <DeleteIcon />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}
      </div>
      <div className="flex h-[3.75rem] border-0 border-t border-solid theme-border-secondary">
        <Button
          type="primary"
          className="m-auto"
          sizing="h-[40px] w-[160px]"
          onClick={handleSwitchMenu}
        >
          {t('building.buttonCancel')}
        </Button>
      </div>

      <Modal
        open={showDuplicateModal}
        onCancel={handleCancelDuplicate}
        afterClose={() => setDuplicateFiles([])}
        width={600}
        centered
        className="duplicate-options-modal"
        isEnterable
        onEnter={handleConfirmDuplicate}
      >
        <Title
          title={t('building.duplicateFileModal.title')}
          bottomMargin="mb-8"
        />
        <div className="flex flex-col gap-6">
          <Typography className="text-[#1a1a1a] text-[16px] leading-[24px]">
            {duplicateFiles.length > 1
              ? t('building.duplicateFileModal.descriptionMultiple')
              : t('building.duplicateFileModal.description', {
                  fileName: duplicateFiles[0]?.newFile.name
                })}
          </Typography>

          <Radio.Group
            onChange={(e: RadioChangeEvent) =>
              setSelectedDuplicateOption(e.target.value)
            }
            value={selectedDuplicateOption}
            className="w-full"
          >
            <Space direction="vertical" className="w-full gap-4">
              <Radio
                value={1}
                className="text-[16px] flex items-center custom-radio-large"
              >
                <span className="text-[16px]">
                  {t('building.duplicateFileModal.replace')}
                </span>
              </Radio>
              <Radio
                value={2}
                className="text-[16px] flex items-center custom-radio-large"
              >
                <span className="text-[16px]">
                  {t('building.duplicateFileModal.keepBoth')}
                </span>
              </Radio>
            </Space>
          </Radio.Group>

          {selectedDuplicateOption === 1 && (
            <Typography className="text-red-500 text-[16px] leading-[24px] italic">
              {t('building.duplicateFileModal.overwriteNote')}
            </Typography>
          )}

          <div className="flex justify-center gap-10 mt-6">
            <Button sizing="w-36 h-12" onClick={handleCancelDuplicate}>
              {t('building.buttonCancel')}
            </Button>
            <Button
              type="primary"
              sizing="w-36 h-12"
              onClick={handleConfirmDuplicate}
            >
              {t('building.duplicateFileModal.upload')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
